// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { countryIsUsOrCa } from "@flip/App/Store/Selectors/GeneralSelectors";
import { btFormId, mcFormId, stFormId } from "@flip/Overrides/Widgets/CreateAccount/CreateAccount";
import { AccountFormState } from "@flip/Overrides/Widgets/CreateAccount/CreateAccount.form.module";
import { color, maxLengthAttr, xMask, xScreen } from "@flip/XDL/Utilities";
import { CreateForm } from "@flip/XDL/Utilities/form";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    accountForm?: CreateForm<AccountFormState>;
    countries?: CountryModel[];
    businessTypeList?: any;
    marketSegmentList?: any;
    id?: string;
    formId: string;
    addressesErrors?: any;
    setAddressesErrors?: (obj: any) => void;
}

interface CreateAccountUserFormStyles {
    createAccountSubTitle?: TypographyProps;
    gridContainer: GridContainerProps;
    fieldGridItem?: GridItemProps;
    lineGridItem?: GridItemProps;
    entityNameField?: TextFieldPresentationProps;
    addressGridItem?: GridItemProps;
    stateItem?: InjectableCss;
    cityGridItem?: GridItemProps;
    zipGridItem?: GridItemProps;
    entityItem?: GridItemProps;
    useBillingItem?: InjectableCss;
    uploadCreditApplicationDescriptionGridItem?: GridItemProps;
    uploadCreditApplicationGridItem?: GridItemProps;
    countryItem?: InjectableCss;
}

const createAccountUserFormStyles: CreateAccountUserFormStyles = {
    gridContainer: {},
    fieldGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    lineGridItem: {
        width: 12,
        css: css`
            align-items: baseline;
            flex-wrap: wrap;

            p {
                margin-top: 24px;
            }
            &:after {
                content: "";
                display: block;
                width: 100%;
                height: 3px;
                background-color: ${color("common.border")};
                margin-bottom: 16px;
            }
        `,
    },
    entityItem: {
        width: [12, 12, 6, 6, 6],
        css: css``,
    },
    useBillingItem: {
        css: css`
            display: inline-block;
            margin-left: 15px;
            legend {
                display: none;
            }
        `,
    },
    countryItem: {
        css: css``,
    },

    stateItem: {
        css: css``,
    },
    cityGridItem: {
        css: css`
            width: 50%;
            max-width: 100%;
        `,
    },
    zipGridItem: {
        css: css`
            width: 50%;
            max-width: 100%;
        `,
    },
    createAccountSubTitle: {
        variant: "p",
        css: css`
            width: fit-content;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
};

const styles = createAccountUserFormStyles;
const CreateAccountLongForm: FC<OwnProps> = ({
    id,
    countries,
    businessTypeList,
    marketSegmentList,
    accountForm,
    formId,
    addressesErrors,
    setAddressesErrors,
}) => {
    const [selectedBtCountry, setSelectedBtCountry] = useState<CountryModel>();
    const [selectedStCountry, setSelectedStCountry] = useState<CountryModel>();

    if (!accountForm) {
        return null;
    }
    const { fields, isSubmitted } = accountForm.state;
    const useBillingAddress = (value: boolean) => {
        accountForm?.onFieldChange(fields.useBillingAddressForShipping, value);
        if (value) {
            accountForm?.onFieldChange(fields.sAddress1, fields.bAddress1.value);
            accountForm?.onFieldChange(fields.sAddress2, fields.bAddress2.value);
            accountForm?.onFieldChange(fields.sCity, fields.bCity.value);
            accountForm?.onFieldChange(fields.sPostalCode, fields.bPostalCode.value);

            const stCountry = countries?.find((country: CountryModel) => country.name === selectedBtCountry?.name);

            if (stCountry) {
                setSelectedStCountry(stCountry);
                accountForm?.onFieldChange(fields.sCountry, stCountry.name);
                accountForm?.onFieldChange(fields.sState, fields.bState.value);
            }
        }
    };

    return (
        <>
            <GridItem {...styles.fieldGridItem}>
                <TextField
                    value={fields.website.value}
                    onChange={e => accountForm?.onFieldChange(fields.website, e.currentTarget.value)}
                    {...fields.website.attrs}
                    {...{ formId }}
                />
            </GridItem>
            <GridItem {...styles.fieldGridItem}>
                <Select
                    label={translate("Business Type")}
                    value={fields.businessType.value}
                    onChange={e => accountForm?.onFieldChange(fields.businessType, e.currentTarget.value)}
                    data-test-selector="BusinessType"
                    required={true}
                >
                    <option value=""></option>
                    {businessTypeList?.map((list: any) => (
                        <option key={list.name} value={list.name}>
                            {list.name}
                        </option>
                    ))}
                </Select>
            </GridItem>
            <GridItem {...styles.fieldGridItem}>
                <Select
                    label={translate("Market Segment")}
                    value={fields.marketSpace.value}
                    onChange={e => accountForm?.onFieldChange(fields.marketSpace, e.currentTarget.value)}
                    data-test-selector="MarketSegment"
                    required={true}
                >
                    <option value=""></option>
                    {marketSegmentList?.map((list: any) => (
                        <option key={list.name} value={list.name}>
                            {list.name}
                        </option>
                    ))}
                </Select>
            </GridItem>
            <GridItem {...styles.fieldGridItem}>
                <TextField
                    value={fields.title.value}
                    onChange={e => accountForm?.onFieldChange(fields.title, e.currentTarget.value)}
                    {...fields.title.attrs}
                    {...{ formId }}
                />
            </GridItem>
            <GridItem {...styles.entityItem}>
                <RadioGroup
                    name="isUseEntity"
                    value={`${fields.useIntity.value}`}
                    onChangeHandler={e => accountForm?.onFieldChange(fields.useIntity, e.currentTarget.value)}
                    horizontal={true}
                    label={siteMessage("Flip_Entity_Ownership_Information")}
                >
                    <Radio value="false">{translate("No")}</Radio>
                    <Radio value="true">{translate("Yes")}</Radio>
                </RadioGroup>
            </GridItem>
            {fields.useIntity.value === "true" && (
                <GridItem {...styles.entityItem}>
                    <TextField
                        {...styles.entityNameField}
                        value={fields.entityNameField.value}
                        onChange={e => accountForm?.onFieldChange(fields.entityNameField, e.currentTarget.value)}
                        {...fields.entityNameField.attrs}
                        {...{ formId }}
                    />
                </GridItem>
            )}
            {/** Main Contact  */}
            <GridItem width={12}>
                <GridContainer gap={20}>
                    <GridItem {...styles.lineGridItem}>
                        <Typography {...styles.createAccountSubTitle}>
                            {translate("Main Contact (If different from Account Holder)")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <TextField
                            value={fields.mainContactFirstName.value}
                            onChange={e =>
                                accountForm?.onFieldChange(fields.mainContactFirstName, e.currentTarget.value)
                            }
                            {...fields.mainContactFirstName.attrs}
                            {...{ formId: mcFormId }}
                        />
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <TextField
                            value={fields.mainContactLastName.value}
                            onChange={e =>
                                accountForm?.onFieldChange(fields.mainContactLastName, e.currentTarget.value)
                            }
                            {...fields.mainContactLastName.attrs}
                            {...{ formId: mcFormId }}
                        />
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <TextField
                            value={fields.mainContactEmail.value}
                            onChange={e => accountForm?.onFieldChange(fields.mainContactEmail, e.currentTarget.value)}
                            {...fields.mainContactEmail.attrs}
                            type="email"
                            {...{ formId: mcFormId }}
                        />
                    </GridItem>
                    <GridItem {...styles.fieldGridItem}>
                        <TextField
                            value={fields.mainContactPhoneNumber.value}
                            onChange={e =>
                                accountForm?.onFieldChange(fields.mainContactPhoneNumber, e.currentTarget.value)
                            }
                            {...fields.mainContactPhoneNumber.attrs}
                            type="phone"
                            {...{ formId: mcFormId }}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem width={12}>
                <GridContainer gap={20}>
                    {/** Account Payable */}
                    <GridItem width={[12, 12, 6, 6, 6]}>
                        <GridContainer gap={20}>
                            <GridItem {...styles.lineGridItem}>
                                <Typography {...styles.createAccountSubTitle}>
                                    {translate("Account Payable")}
                                </Typography>
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.bAddress1.value}
                                    onChange={e => {
                                        const temp = e.currentTarget.value;
                                        accountForm?.onFieldChange(fields.bAddress1, e.currentTarget.value);
                                        if (fields.useBillingAddressForShipping.value === true) {
                                            accountForm?.onFieldChange(fields.sAddress1, temp);
                                        }
                                    }}
                                    {...fields.bAddress1.attrs}
                                    type="address"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.bAddress2.value}
                                    onChange={e => {
                                        const temp = e.currentTarget.value;
                                        accountForm?.onFieldChange(fields.bAddress2, e.currentTarget.value);
                                        if (fields.useBillingAddressForShipping.value === true) {
                                            accountForm?.onFieldChange(fields.sAddress2, temp);
                                        }
                                    }}
                                    {...fields.bAddress2.attrs}
                                    type="address"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                            {countries && (
                                <>
                                    <GridItem width={6}>
                                        {countries.length > 0 && (
                                            <Select
                                                {...styles.countryItem}
                                                label={translate("Country")}
                                                value={fields.bCountry.value}
                                                onChange={e => {
                                                    const country = countries.find(
                                                        (country: CountryModel) =>
                                                            country.name === e.currentTarget.value,
                                                    );
                                                    const internalAddressErrors = structuredClone(addressesErrors);

                                                    if (country) {
                                                        accountForm?.onFieldChange(fields.bCountry, country.name);
                                                        setSelectedBtCountry(country);
                                                        if (fields.useBillingAddressForShipping.value === true) {
                                                            accountForm?.onFieldChange(fields.sCountry, country.name);
                                                            setSelectedStCountry(country);
                                                            delete internalAddressErrors["sCountry"];
                                                        }
                                                        delete internalAddressErrors["bCountry"];
                                                    } else {
                                                        accountForm?.onFieldChange(fields.bCountry, null);
                                                    }
                                                    setAddressesErrors?.(internalAddressErrors);
                                                }}
                                                data-test-selector="bt-country"
                                                required={true}
                                                error={addressesErrors.bCountry}
                                            >
                                                <option value=""></option>
                                                {countries?.map(country => (
                                                    <option key={country.abbreviation} value={country.name}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    </GridItem>
                                    <GridItem width={6}>
                                        <Select
                                            label={translate("State")}
                                            {...styles.stateItem}
                                            value={fields.bState.value}
                                            onChange={e => {
                                                const state = selectedBtCountry?.states?.find(
                                                    (state: StateModel) => state.name === e.currentTarget.value,
                                                );
                                                const internalAddressErrors = structuredClone(addressesErrors);
                                                if (state) {
                                                    accountForm?.onFieldChange(fields.bState, state.name);

                                                    delete internalAddressErrors["bState"];
                                                    if (fields.useBillingAddressForShipping.value === true) {
                                                        accountForm?.onFieldChange(fields.sState, state.name);

                                                        delete internalAddressErrors["sState"];
                                                    }
                                                } else {
                                                    accountForm?.onFieldChange(fields.bState, null);
                                                }
                                                setAddressesErrors?.(internalAddressErrors);
                                            }}
                                            data-test-selector="bt-state"
                                            required={true}
                                            error={addressesErrors.bState}
                                        >
                                            <option value=""></option>
                                            {selectedBtCountry?.states?.map(state => (
                                                <option key={state.id} value={state.name}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                </>
                            )}
                            <GridItem width={6}>
                                <TextField
                                    value={fields.bCity.value}
                                    onChange={e => {
                                        const temp = e.currentTarget.value;
                                        accountForm?.onFieldChange(fields.bCity, e.currentTarget.value);
                                        if (fields.useBillingAddressForShipping.value === true) {
                                            accountForm?.onFieldChange(fields.sCity, temp);
                                        }
                                    }}
                                    {...fields.bCity.attrs}
                                    type="city"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.bPostalCode.value}
                                    onChange={e => {
                                        const temp = e.currentTarget.value;
                                        accountForm?.onFieldChange(fields.bPostalCode, e.currentTarget.value);
                                        if (fields.useBillingAddressForShipping.value === true) {
                                            accountForm?.onFieldChange(fields.sPostalCode, temp);
                                        }
                                    }}
                                    {...fields.bPostalCode.attrs}
                                    type="postalCode"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.financeContactName.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.financeContactName, e.currentTarget.value)
                                    }
                                    {...fields.financeContactName.attrs}
                                    type="fullName"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.financePhoneNumber.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.financePhoneNumber, e.currentTarget.value)
                                    }
                                    {...fields.financePhoneNumber.attrs}
                                    type="phone"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.financeContactEmail.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.financeContactEmail, e.currentTarget.value)
                                    }
                                    {...fields.financeContactEmail.attrs}
                                    type="email"
                                    {...{ formId: btFormId }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {/** Shipping Information */}
                    <GridItem width={[12, 12, 6, 6, 6]}>
                        <GridContainer gap={20}>
                            <GridItem {...styles.lineGridItem}>
                                <Typography {...styles.createAccountSubTitle}>
                                    {translate("Shipping Information")}
                                </Typography>
                                <CheckboxGroup {...styles.useBillingItem}>
                                    <Checkbox
                                        checked={fields.useBillingAddressForShipping.value}
                                        onChange={(e, value) => useBillingAddress(value)}
                                    >
                                        {translate("Use billing address?")}
                                    </Checkbox>
                                </CheckboxGroup>
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    disabled={fields.useBillingAddressForShipping.value === true}
                                    value={fields.sAddress1.value}
                                    onChange={e => {
                                        accountForm?.onFieldChange(fields.sAddress1, e.currentTarget.value);
                                    }}
                                    {...fields.sAddress1.attrs}
                                    type="address"
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    disabled={fields.useBillingAddressForShipping.value === true}
                                    value={fields.sAddress2.value}
                                    onChange={e => accountForm?.onFieldChange(fields.sAddress2, e.currentTarget.value)}
                                    {...fields.sAddress2.attrs}
                                    type="address"
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                            {countries && (
                                <GridItem width={6}>
                                    {countries.length > 0 && (
                                        <Select
                                            disabled={fields.useBillingAddressForShipping.value === true}
                                            {...styles.countryItem}
                                            label={translate("Country")}
                                            value={fields.sCountry.value}
                                            onChange={e => {
                                                accountForm?.onFieldChange(fields.sCountry, e.currentTarget.value);
                                                const temp = countries.find(
                                                    country => country.name === e.currentTarget.value,
                                                );
                                                setSelectedStCountry(temp);
                                                setAddressesErrors?.({
                                                    ...addressesErrors,
                                                    sCountry: undefined,
                                                });
                                            }}
                                            data-test-selector="st-country"
                                            error={addressesErrors.sCountry}
                                            required={true}
                                        >
                                            <option value=""></option>
                                            {countries?.map(country => (
                                                <option key={country.abbreviation} value={country.name}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                </GridItem>
                            )}
                            {countries && (
                                <GridItem width={6}>
                                    <Select
                                        disabled={fields.useBillingAddressForShipping.value === true}
                                        label={translate("State")}
                                        {...styles.stateItem}
                                        value={fields.sState.value}
                                        onChange={e => {
                                            accountForm?.onFieldChange(fields.sState, e.currentTarget.value);
                                            setAddressesErrors?.({
                                                ...addressesErrors,
                                                sState: undefined,
                                            });
                                        }}
                                        data-test-selector="st-state"
                                        required={true}
                                        error={addressesErrors.sState}
                                    >
                                        <option value=""></option>
                                        {selectedStCountry?.states?.map(state => (
                                            <option key={state.id} value={state.name}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            )}
                            <GridItem width={6}>
                                <TextField
                                    disabled={fields.useBillingAddressForShipping.value === true}
                                    value={fields.sCity.value}
                                    onChange={e => accountForm?.onFieldChange(fields.sCity, e.currentTarget.value)}
                                    {...fields.sCity.attrs}
                                    type="city"
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    disabled={fields.useBillingAddressForShipping.value === true}
                                    value={fields.sPostalCode.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.sPostalCode, e.currentTarget.value)
                                    }
                                    {...fields.sPostalCode.attrs}
                                    type="postalCode"
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.sContactName.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.sContactName, e.currentTarget.value)
                                    }
                                    {...fields.sContactName.attrs}
                                    type="fullName"
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.sContactPhoneNumber.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.sContactPhoneNumber, e.currentTarget.value)
                                    }
                                    {...fields.sContactPhoneNumber.attrs}
                                    type="phone"
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                            <GridItem width={6}>
                                <TextField
                                    value={fields.sContactAccountNumber.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.sContactAccountNumber, e.currentTarget.value)
                                    }
                                    {...fields.sContactAccountNumber.attrs}
                                    {...{ formId: stFormId }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </>
    );
};

export default CreateAccountLongForm;
